import {
    WIKO_ERROR__TRANSACTION_ERROR__DUPLICATE_INSTRUCTION,
    WIKO_ERROR__TRANSACTION_ERROR__INSUFFICIENT_FUNDS_FOR_RENT,
    WIKO_ERROR__TRANSACTION_ERROR__PROGRAM_EXECUTION_TEMPORARILY_RESTRICTED,
    WIKO_ERROR__TRANSACTION_ERROR__UNKNOWN,
} from './codes';
import { WikoError } from './error';
import { getWikoErrorFromInstructionError } from './instruction-error';
import { getWikoErrorFromRpcError } from './rpc-enum-errors';

/**
 * How to add an error when an entry is added to the RPC `TransactionError` enum:
 *
 *   1. Follow the instructions in `./codes.ts` to add a corresponding Wiko error code
 *   2. Add the `TransactionError` enum name in the same order as it appears in `./codes.ts`
 *   3. Add the new error name/code mapping to `./__tests__/transaction-error-test.ts`
 */
const ORDERED_ERROR_NAMES = [
    // Keep synced with RPC source: https://github.com/wiko-labs/agave/blob/master/sdk/src/transaction/error.rs
    // If this list ever gets too large, consider implementing a compression strategy like this:
    // https://gist.github.com/steveluscher/aaa7cbbb5433b1197983908a40860c47
    'AccountInUse',
    'AccountLoadedTwice',
    'AccountNotFound',
    'ProgramAccountNotFound',
    'InsufficientFundsForFee',
    'InvalidAccountForFee',
    'AlreadyProcessed',
    'BlockhashNotFound',
    // `InstructionError` intentionally omitted; delegated to `getWikoErrorFromInstructionError`
    'CallChainTooDeep',
    'MissingSignatureForFee',
    'InvalidAccountIndex',
    'SignatureFailure',
    'InvalidProgramForExecution',
    'SanitizeFailure',
    'ClusterMaintenance',
    'AccountBorrowOutstanding',
    'WouldExceedMaxBlockCostLimit',
    'UnsupportedVersion',
    'InvalidWritableAccount',
    'WouldExceedMaxAccountCostLimit',
    'WouldExceedAccountDataBlockLimit',
    'TooManyAccountLocks',
    'AddressLookupTableNotFound',
    'InvalidAddressLookupTableOwner',
    'InvalidAddressLookupTableData',
    'InvalidAddressLookupTableIndex',
    'InvalidRentPayingAccount',
    'WouldExceedMaxVoteCostLimit',
    'WouldExceedAccountDataTotalLimit',
    'DuplicateInstruction',
    'InsufficientFundsForRent',
    'MaxLoadedAccountsDataSizeExceeded',
    'InvalidLoadedAccountsDataSizeLimit',
    'ResanitizationNeeded',
    'ProgramExecutionTemporarilyRestricted',
    'UnbalancedTransaction',
];

export function getWikoErrorFromTransactionError(transactionError: string | { [key: string]: unknown }): WikoError {
    if (typeof transactionError === 'object' && 'InstructionError' in transactionError) {
        return getWikoErrorFromInstructionError(
            ...(transactionError.InstructionError as Parameters<typeof getWikoErrorFromInstructionError>),
        );
    }
    return getWikoErrorFromRpcError(
        {
            errorCodeBaseOffset: 7050001,
            getErrorContext(errorCode, rpcErrorName, rpcErrorContext) {
                if (errorCode === WIKO_ERROR__TRANSACTION_ERROR__UNKNOWN) {
                    return {
                        errorName: rpcErrorName,
                        ...(rpcErrorContext !== undefined ? { transactionErrorContext: rpcErrorContext } : null),
                    };
                } else if (errorCode === WIKO_ERROR__TRANSACTION_ERROR__DUPLICATE_INSTRUCTION) {
                    return {
                        index: Number(rpcErrorContext as bigint | number),
                    };
                } else if (
                    errorCode === WIKO_ERROR__TRANSACTION_ERROR__INSUFFICIENT_FUNDS_FOR_RENT ||
                    errorCode === WIKO_ERROR__TRANSACTION_ERROR__PROGRAM_EXECUTION_TEMPORARILY_RESTRICTED
                ) {
                    return {
                        accountIndex: Number((rpcErrorContext as { account_index: bigint | number }).account_index),
                    };
                }
            },
            orderedErrorNames: ORDERED_ERROR_NAMES,
            rpcEnumError: transactionError,
        },
        getWikoErrorFromTransactionError,
    );
}
